import React, { useState } from 'react';
import SEO from '../components/SEO';

const PHONE = '+18885098604';
const PHONE_DISPLAY = '+1 (888) 509-8604';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'TravelAgency',
  name: 'USAFlightsDeals — Expedia Support & Flight Booking Assistance',
  url: 'https://usaflightsdeal.com/expedia',
  description: 'Get fast help with Expedia flight reservations, cancellations, booking changes, refunds, and travel itinerary support.',
  telephone: PHONE,
  email: 'support@usaflightsdeal.com',
};

const SERVICES = [
  { icon: '✈️', title: 'Flight Reservations', desc: 'Book domestic and international flights with assistance for one-way, round-trip, multi-city, and last-minute travel.' },
  { icon: '🔄', title: 'Flight Changes', desc: 'Need to modify your itinerary? Get assistance with same-day flight changes, date updates, and rebooking.' },
  { icon: '❌', title: 'Cancellation Support', desc: 'Contact our support team for cancellation guidance, refund information, and travel credit assistance.' },
  { icon: '💺', title: 'Business Class Flights', desc: 'Explore premium travel options with business and first-class booking assistance for international routes.' },
  { icon: '🏨', title: 'Hotel Reservations', desc: 'Get help with hotel bookings, itinerary updates, room modifications, and vacation package support.' },
  { icon: '🕐', title: '24/7 Travel Assistance', desc: 'Our travel support line is available for urgent booking concerns and last-minute itinerary updates.' },
];

const WHY_CALL = [
  { icon: '⚡', title: 'Fast Booking Help', desc: 'Quick support for reservations, cancellations, and flight itinerary updates.' },
  { icon: '🔧', title: 'Travel Change Assistance', desc: 'Get help modifying dates, schedules, routes, and passenger details.' },
  { icon: '🌴', title: 'Vacation Support', desc: 'Support for flights, hotels, vacation packages, and premium travel bookings.' },
];

const TRUSTED_LIST = [
  'Flight Reservations',
  'Expedia Booking Support',
  'Flight Cancellation Assistance',
  'Expedia Refund Requests',
  'Itinerary Changes',
  'Hotel Booking Support',
  'Last-Minute Flight Changes',
  'Travel Credit Assistance',
  'Airline Schedule Changes',
  'Vacation Package Support',
];

const POPULAR_SERVICES = [
  'Expedia flight reservation support',
  'Expedia booking assistance',
  'Expedia refund request help',
  'Expedia cancellation support',
  'Expedia contact number assistance',
  'Expedia travel itinerary changes',
  'Expedia hotel reservation support',
  'Expedia vacation package help',
];

const PHONE_SUPPORT_LIST = [
  'Flight reservations',
  'Existing Expedia bookings',
  'Refund requests',
  'Cancellations',
  'Travel changes',
  'Airline ticket support',
  'Hotel modifications',
  'Vacation packages',
];

const FLIGHT_LIST = [
  'Domestic flight reservations',
  'International flight bookings',
  'Multi-city trips',
  'Business class reservations',
  'Last-minute flight deals',
  'Flight changes and upgrades',
  'Group travel assistance',
];

const FLIGHT_WHY = [
  'Fast booking assistance',
  'Live agent support',
  'Easy itinerary modifications',
  'Travel issue resolution',
  'Refund guidance',
];

const REFUND_LIST = [
  'Cancelled flight refunds',
  'Airline refund status',
  'Travel credits',
  'Hotel refund requests',
  'Vacation package refunds',
  'Refund processing assistance',
];

const CONTACT_LIST = [
  'Flight reservation support',
  'Booking modifications',
  'Cancellations and refunds',
  'Hotel reservation changes',
  'Travel itinerary issues',
  'Last-minute travel assistance',
];

const FAQS = [
  { q: 'How can I get help with my Expedia booking?', a: `Call ${PHONE_DISPLAY} for flight reservations, booking support, cancellations, and travel itinerary assistance.` },
  { q: 'Can I get help with flight changes?', a: 'Yes. Our support team helps travelers with flight rebooking, schedule changes, and itinerary modifications.' },
  { q: 'Do you assist with cancellations and refunds?', a: 'Yes. Contact our travel assistance line for cancellation guidance and refund-related support.' },
  { q: 'Can I book business class flights?', a: 'Yes. We help travelers explore business and first-class booking options for domestic and international travel.' },
  { q: 'How do I contact Expedia customer service?', a: 'You can contact travel booking assistance for help with reservations, cancellations, refunds, and itinerary changes by calling the support number listed above.' },
  { q: 'Can I change my Expedia flight reservation?', a: 'Yes, flight changes depend on airline fare rules and ticket conditions.' },
  { q: 'How long do Expedia refunds take?', a: 'Refund timelines vary depending on the airline, hotel, or travel provider policies.' },
  { q: 'Can I cancel an Expedia booking?', a: 'Cancellation policies depend on the booking type and provider terms.' },
];

const SEO_TAGS = [
  'Expedia customer service', 'Expedia phone number', 'Expedia contact number',
  'Contact Expedia', 'Expedia refund', 'Expedia booking',
  'Expedia reservation help', 'Expedia cancellation support',
  'Expedia live agent', 'Expedia flight booking support',
];

export default function ExpediaPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="font-sans text-gray-800">
      <SEO
        title="Expedia Support & Flight Booking Assistance | USAFlightsDeals"
        canonical="/expedia"
        description={`Get fast help with Expedia flight reservations, cancellations, booking changes, and refunds. Call ${PHONE_DISPLAY} for 24/7 live travel support.`}
        keywords="expedia support, expedia customer service, expedia phone number, expedia booking assistance, expedia flight help, expedia refund support"
        jsonLd={jsonLd}
      />

      {/* ── Hero ── */}
      <div className="bg-gradient-to-br from-blue-950 to-blue-800 text-white py-16 sm:py-24 px-4 text-center">
        <p className="text-cyan-300 font-semibold text-sm uppercase tracking-widest mb-3">Need Immediate Expedia Booking Assistance?</p>
        <h1 className="text-3xl sm:text-5xl font-extrabold leading-tight mb-5">
          Expedia Support &amp; Flight<br className="hidden sm:block" /> Booking Assistance
        </h1>
        <p className="text-blue-200 text-lg max-w-2xl mx-auto mb-8">
          Get fast help with flight reservations, cancellations, booking changes, refunds, business class flights, and travel itinerary support.
        </p>
        <a
          href={`tel:${PHONE}`}
          className="inline-flex items-center gap-3 bg-white text-blue-900 font-extrabold px-10 py-4 rounded-2xl text-xl shadow-2xl hover:bg-cyan-50 transition-colors"
        >
          📞 Call Now: {PHONE_DISPLAY}
        </a>
      </div>

      {/* ── Travel Assistance Services ── */}
      <section className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2 text-center">Travel Assistance Services</h2>
        <p className="text-gray-500 text-center mb-10">Professional support for Expedia-related travel bookings and itinerary assistance.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map(s => (
            <div key={s.title} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">{s.icon}</div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Live Rep Banner ── */}
      <div className="bg-blue-600 text-white py-12 px-4 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Speak With a Live Travel Support Representative</h2>
        <p className="text-blue-100 text-lg max-w-xl mx-auto mb-7">Avoid long hold times and get quick travel booking support for Expedia-related reservations.</p>
        <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 bg-white text-blue-800 font-extrabold px-10 py-4 rounded-2xl text-xl shadow-xl hover:bg-cyan-50 transition-colors">
          📞 Call Now: {PHONE_DISPLAY}
        </a>
      </div>

      {/* ── Why Travelers Call Us ── */}
      <section className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-center text-gray-900 mb-10">Why Travelers Call Us</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {WHY_CALL.map(w => (
            <div key={w.title} className="text-center bg-blue-50 border border-blue-100 rounded-2xl p-8">
              <div className="text-4xl mb-4">{w.icon}</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{w.title}</h3>
              <p className="text-gray-500 text-sm">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trusted Assistance Checklist ── */}
      <section className="bg-gray-50 py-14 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 text-center">Trusted Expedia Booking Assistance</h2>
          <p className="text-gray-500 text-center mb-8">Need help with your Expedia booking? Our travel assistance team helps travelers with:</p>
          <div className="grid sm:grid-cols-2 gap-3 mb-10">
            {TRUSTED_LIST.map(item => (
              <div key={item} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-5 py-3 shadow-sm">
                <span className="text-green-500 font-bold text-lg">✔</span>
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-4 rounded-2xl text-lg transition-colors shadow">
              📞 Speak With A Live Agent Now
            </a>
          </div>
        </div>
      </section>

      {/* ── Expedia Customer Service ── */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-5">Expedia Customer Service</h2>
        <p className="text-gray-600 text-lg leading-relaxed mb-8">
          Looking for Expedia customer service assistance? Travelers often search for quick support regarding flight reservations, cancellations, refunds, and booking modifications. Our support assistance line connects you with live travel agents who can help resolve your travel concerns quickly.
        </p>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Expedia Support Services</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {POPULAR_SERVICES.map(item => (
            <div key={item} className="flex items-center gap-3">
              <span className="text-blue-500 font-bold">▸</span>
              <span className="text-gray-700">{item}</span>
            </div>
          ))}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-2xl px-8 py-6 inline-block">
          <a href={`tel:${PHONE}`} className="text-2xl font-extrabold text-blue-700 hover:text-blue-800 transition-colors flex items-center gap-3">
            📞 {PHONE_DISPLAY}
          </a>
        </div>
      </section>

      {/* ── Phone Number Support ── */}
      <section className="bg-blue-950 text-white py-14 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-4">Expedia Phone Number Support</h2>
            <p className="text-blue-200 text-lg mb-6">Searching for an Expedia phone number for booking support? Get fast help for:</p>
            <ul className="space-y-2">
              {PHONE_SUPPORT_LIST.map(item => (
                <li key={item} className="flex items-center gap-3 text-blue-100">
                  <span className="text-cyan-400 font-bold">✔</span>{item}
                </li>
              ))}
            </ul>
            <p className="text-blue-300 text-sm mt-5">Our support team is available to help travelers handle urgent travel concerns efficiently.</p>
          </div>
          <div className="text-center">
            <div className="bg-white/10 border border-white/20 rounded-3xl p-10">
              <p className="text-blue-200 mb-4 font-semibold">Available 24 / 7</p>
              <a href={`tel:${PHONE}`} className="text-3xl font-extrabold text-white hover:text-cyan-300 transition-colors block mb-6">
                📞 {PHONE_DISPLAY}
              </a>
              <a href={`tel:${PHONE}`} className="inline-block bg-white text-blue-900 font-bold px-8 py-3.5 rounded-xl hover:bg-cyan-50 transition-colors">
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Flight Reservation Assistance ── */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">Expedia Flight Reservation Assistance</h2>
        <p className="text-gray-600 text-lg mb-8">Need help booking a flight? We assist travelers with:</p>
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <ul className="space-y-2">
            {FLIGHT_LIST.map(item => (
              <li key={item} className="flex items-center gap-3 text-gray-700">
                <span className="text-green-500 font-bold">✔</span>{item}
              </li>
            ))}
          </ul>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Why Travelers Call For Expedia Flight Support</h3>
            <ul className="space-y-2">
              {FLIGHT_WHY.map(item => (
                <li key={item} className="flex items-center gap-3 text-gray-700">
                  <span className="text-blue-500 font-bold">▸</span>{item}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-xl text-lg transition-colors">
          📞 Call Expedia Booking Assistance
        </a>
      </section>

      {/* ── Refund Support ── */}
      <section className="bg-gray-50 py-14 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">Expedia Refund Support</h2>
          <p className="text-gray-600 text-lg mb-6">Need help with an Expedia refund request? Travelers contact support for:</p>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {REFUND_LIST.map(item => (
              <div key={item} className="flex items-center gap-3">
                <span className="text-green-500 font-bold">✔</span>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-500 text-sm italic">Refund eligibility may depend on airline policies, fare conditions, and booking terms.</p>
        </div>
      </section>

      {/* ── Contact Expedia Support ── */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">Contact Expedia Support Assistance</h2>
        <p className="text-gray-600 text-lg mb-4">
          Travelers searching for "contact Expedia", "Expedia contact number", or "Expedia customer service phone number" often need urgent assistance with reservations and booking changes.
        </p>
        <p className="text-gray-600 text-lg mb-6">Our travel support assistance line helps with:</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-10">
          {CONTACT_LIST.map(item => (
            <div key={item} className="flex items-center gap-3">
              <span className="text-blue-500 font-bold">▸</span>
              <span className="text-gray-700">{item}</span>
            </div>
          ))}
        </div>
        <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-xl text-lg transition-colors">
          📞 Call Now For Immediate Expedia Booking Assistance
        </a>
      </section>

      {/* ── Immediate Assistance Banner ── */}
      <div className="bg-gradient-to-r from-blue-700 to-cyan-600 text-white py-14 px-4 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Need Immediate Travel Assistance?</h2>
        <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">
          Speak with a travel support specialist today for booking help, cancellations, itinerary updates, and premium flight assistance.
        </p>
        <a href={`tel:${PHONE}`} className="inline-flex items-center gap-3 bg-white text-blue-900 font-extrabold px-10 py-4 rounded-2xl text-xl shadow-xl hover:bg-cyan-50 transition-colors">
          📞 {PHONE_DISPLAY}
        </a>
      </div>

      {/* ── FAQs ── */}
      <section className="max-w-4xl mx-auto px-4 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full px-6 py-4 text-left flex justify-between items-center font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
              >
                {faq.q}
                <span className={`text-gray-400 ml-4 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>▼</span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5 pt-1 text-gray-600 text-sm leading-relaxed border-t border-gray-100">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── SEO Tags Section ── */}
      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Expedia Travel Support — Common Search Topics</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-5">
            Travelers searching online for Expedia customer service, Expedia phone number, Expedia flight reservation, Expedia refund support, or Expedia booking help often need immediate live assistance for urgent travel issues. This support page provides fast access to live travel booking assistance for reservations, cancellations, refunds, itinerary changes, and booking support.
          </p>
          <div className="flex flex-wrap gap-2">
            {SEO_TAGS.map(tag => (
              <span key={tag} className="bg-white border border-gray-200 text-gray-600 text-xs px-3 py-1.5 rounded-full">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="bg-blue-950 text-white py-12 px-4 text-center">
        <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 bg-white text-blue-900 font-extrabold px-10 py-4 rounded-2xl text-xl hover:bg-cyan-50 transition-colors shadow-xl">
          📞 Call Expedia Support Now
        </a>
        <p className="text-blue-400 text-xs mt-6 max-w-2xl mx-auto">
          This is a third-party travel support service and is not affiliated with Expedia, Inc. Expedia® is a registered trademark of Expedia, Inc.
        </p>
      </section>
    </div>
  );
}
